import React from 'react'
import { Box, Skeleton, Stack, Zoom } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import ParentPopup from './ParentPopup'

interface PopupProps {
  content?: React.ReactNode
  loading?: boolean
  sitecolor?: string
  parentDevice?: unknown | null
  offset?: { x: number; y: number }
  onPopupEnter?: () => void
  onPopupLeave?: () => void
  onDetailsClick?: () => void
}

const Period = ({ delay }: { delay: number }) => (
  <Box
    sx={{
      width: 4,
      height: 4,
      borderRadius: '50%',
      backgroundColor: 'currentColor',
      animation: 'pulse 1.5s ease-in-out infinite',
      animationDelay: `${delay}ms`,
      '@keyframes pulse': {
        '0%, 80%, 100%': {
          opacity: 0.3,
        },
        '40%': {
          opacity: 1,
        },
      },
    }}
  />
)

const PopupPosition: React.FC<{
  offset: { x: number; y: number }
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  children: React.ReactNode
}> = ({ offset, onMouseEnter, onMouseLeave, children }) => (
  <Box
    sx={{
      position: 'absolute',
      left: `${offset.x}px`,
      top: `${offset.y}px`,
      zIndex: 1000,
    }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {children}
  </Box>
)

const Popup: React.FC<PopupProps> = ({
  content,
  loading = false,
  sitecolor,
  parentDevice = null,
  offset,
  onPopupEnter,
  onPopupLeave,
}) => {
  const theme = useTheme()

  if (!offset) {
    if (loading) {
      return (
        <Box
          sx={{
            position: 'absolute',
            bottom: 24,
            left: 24,
            width: 280,
            backgroundColor: theme.palette.common.white,
            borderRadius: '24px',
            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
            p: 2,
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
        >
          <Stack spacing={1.5}>
            <Skeleton variant="rounded" width="100%" height={16} />
            <Skeleton variant="text" width="60%" height={20} />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="text" width={120} height={16} />
              <Skeleton variant="text" width={40} height={16} sx={{ ml: 'auto' }} />
            </Stack>
          </Stack>
        </Box>
      )
    }

    return (
      <Box
        sx={{
          position: 'absolute',
          bottom: 24,
          left: 24,
          minWidth: parentDevice ? 'auto' : 280,
          width: parentDevice ? 'fit-content' : undefined,
          backgroundColor: theme.palette.common.white,
          borderRadius: '16px',
          border: '1px solid #EFEFEF',
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
          p: parentDevice ? 0 : 2,
          zIndex: 1000,
          pointerEvents: 'auto',
        }}
        onMouseEnter={onPopupEnter}
        onMouseLeave={onPopupLeave}
      >
        {parentDevice ? <ParentPopup device={parentDevice} /> : content}
      </Box>
    )
  }

  return (
    <PopupPosition offset={offset} onMouseEnter={onPopupEnter} onMouseLeave={onPopupLeave}>
      <Box>
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              backgroundColor: theme.palette.common.white,
              borderRadius: '8px',
              boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
              p: 2,
              color: sitecolor || theme.palette.primary.main,
            }}
          >
            <Period delay={0} />
            <Period delay={300} />
            <Period delay={600} />
          </Box>
        ) : parentDevice ? (
          <Zoom in={true}>
            <Box
              sx={{
                backgroundColor: 'white !important',
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                opacity: 1,
                zIndex: 1000,
                border: '1px solid #E0E0E0',
              }}
            >
              <ParentPopup device={parentDevice} />
            </Box>
          </Zoom>
        ) : (
          <Zoom in={true}>
            <Box
              sx={{
                backgroundColor: 'white !important',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                opacity: 1,
                zIndex: 1000,
              }}
            >
              {content}
            </Box>
          </Zoom>
        )}
      </Box>
    </PopupPosition>
  )
}

export default Popup